import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { filterNav, NAV_ITEMS } from '@/app/layout/nav';
import { useAuthStore } from '@/features/auth/auth.store';
import { usePermissionChecker } from '@/features/auth/permissions';
import { accentForPath } from '@/shared/lib/nav-accent';

export interface PageHit {
  key: string;
  title: string;
  /** Sekcja nadrzędna (np. „Analityka”) dla pozycji podmenu. */
  parent: string | null;
  to: string;
  icon: LucideIcon;
  color: string;
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l');

/** Widoki aplikacji dostępne dla użytkownika (z menu), do wyszukiwania po nazwie i „szybkich przejść”. */
export function usePageHits() {
  const { t } = useI18n();
  const can = usePermissionChecker();
  const features = useAuthStore((s) => s.features);

  const pages = useMemo<PageHit[]>(() => {
    const out: PageHit[] = [];
    for (const item of filterNav(NAV_ITEMS, can, features)) {
      const accent = accentForPath(item.to);
      const parentTitle = t(item.labelKey);
      if (item.children?.length) {
        for (const c of item.children) {
          out.push({ key: c.key, title: t(c.labelKey), parent: parentTitle, to: c.to, icon: item.icon, color: c.color ?? accent.color });
        }
      } else {
        out.push({ key: item.key, title: parentTitle, parent: null, to: item.to, icon: item.icon, color: accent.color });
      }
    }
    return out;
  }, [can, features, t]);

  /** Dopasowanie bez rozróżniania wielkości liter i polskich znaków („zarzad” → „Zarząd”). */
  const match = (query: string, limit = 5): PageHit[] => {
    const q = norm(query.trim());
    if (!q) return [];
    return pages.filter((p) => norm(`${p.parent ?? ''} ${p.title}`).includes(q)).slice(0, limit);
  };

  return { pages, match };
}
